import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.tsx'
import { isCompanyOnlyUser, getFirstCompanyId } from '../utils/roles.ts'
import {
  TrendingUp,
  FileText,
  Activity,
  MapPin,
  Search,
  BarChart3,
  Mail,
  Globe,
  Users,
  Shield,
} from 'lucide-react'

const Home: React.FC = () => {
  const { user } = useAuth()

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-50 to-blue-100 py-16 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-5xl font-bold mb-4 text-primary-800">Marzz AI</h1>
          <p className="text-xl mb-2 text-primary-700 font-medium">Local SEO Marketing Platform</p>
          <p className="text-lg mb-8 text-gray-600 max-w-2xl mx-auto">
            Automate your local SEO services with keyword research, Google Business Profile
            management, geographic rank tracking, and shareable client reporting — all in one
            platform.
          </p>
          {!user ? (
            <div className="flex flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="px-8 py-3 bg-white text-primary-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-lg border border-primary-200"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
              >
                Sign Up
              </Link>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/reports"
                className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg"
              >
                View Reports
              </Link>
              <Link
                to={isCompanyOnlyUser(user) ? `/company/${getFirstCompanyId(user)}` : '/companies'}
                className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-lg"
              >
                {isCompanyOnlyUser(user) ? 'View Dashboard' : 'View Companies'}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions for logged-in users */}
      {user && (
        <div className="container mx-auto max-w-5xl px-4 -mt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Activity className="w-8 h-8 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {isCompanyOnlyUser(user) ? 'Dashboard' : 'Analytics'}
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                View keyword metrics, SEO performance, geographic rankings, and profile visibility
                trends.
              </p>
              <Link
                to={isCompanyOnlyUser(user) ? `/company/${getFirstCompanyId(user)}` : '/companies'}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {isCompanyOnlyUser(user) ? 'View Dashboard →' : 'View Companies →'}
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-8 h-8 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Reports</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Generate monthly performance reports with lead data, ROI metrics, and shareable
                dashboard links.
              </p>
              <Link to="/reports" className="text-green-600 hover:text-green-700 font-medium">
                View Reports →
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-8 h-8 text-orange-600" />
                <h3 className="text-lg font-semibold text-gray-900">Generate Report</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Create a new performance report with keyword metrics, rankings, and client
                analytics.
              </p>
              <Link
                to="/setup-report"
                className="text-orange-600 hover:text-orange-700 font-medium"
              >
                Create Report →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Platform Features for public visitors */}
      {!user && (
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Everything You Need for Local SEO
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Built for agencies and freelancers who manage local SEO for multiple clients. Automate
            the work, impress your clients with the reporting.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Search className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Keyword Research</h3>
              <p className="text-gray-600 text-sm">
                AI-powered keyword research to find the best local search terms for your clients'
                industries and locations.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">GBP Management</h3>
              <p className="text-gray-600 text-sm">
                Manage Google Business Profile updates, products, and FAQs with automated post
                scheduling across all client profiles.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Heatmap Rank Tracking</h3>
              <p className="text-gray-600 text-sm">
                Geographic geogrid rank auditing shows exactly where your clients rank on Google
                Search and Maps across their service area.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Performance Analytics</h3>
              <p className="text-gray-600 text-sm">
                Track keyword positions, visibility scores, and ranking trends over time. Compare
                performance across all target keywords.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Shareable Reports</h3>
              <p className="text-gray-600 text-sm">
                Send clients a link to a branded dashboard with their analytics, position maps, and
                keyword performance — no login required.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Agency & White-Label</h3>
              <p className="text-gray-600 text-sm">
                Multi-tier access control for agencies. Manage teams, assign clients to members, and
                white-label the entire platform.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* How It Works for public visitors */}
      {!user && (
        <div className="bg-gray-50 py-16 px-4">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How It Works</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Add Your Clients</h3>
                <p className="text-gray-600 text-sm">
                  Import companies, set target keywords, and define their service area for
                  geographic rank tracking.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Track Performance</h3>
                <p className="text-gray-600 text-sm">
                  Automated data collection monitors keyword rankings, GBP visibility, and
                  geographic position across the search grid.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Share Reports</h3>
                <p className="text-gray-600 text-sm">
                  Generate monthly reports with ROI metrics, lead data, and heatmaps. Share via
                  email with a no-login-required dashboard link.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <Link
                to="/signup"
                className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg"
              >
                Start Free
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Built for section */}
      {!user && (
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Built for Local SEO Professionals
              </h2>
              <p className="text-gray-600 mb-6">
                Whether you're a solo freelancer or a growing agency, Marzz AI scales with your
                business. Manage multiple clients, assign team members, and deliver automated
                monthly performance reports that showcase real ROI.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">
                    3-tier access control: System Admin, Agency Admin, Team Member
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">
                    Monthly reports with leads, revenue, ROI, and projected lifetime value
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">
                    Geogrid heatmaps showing rank position across the entire service area
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">
                    Google Business Profile + Google Local Service Ads tracking
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-blue-100 rounded-xl p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Report Includes</h3>
              <div className="space-y-3 text-sm">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <span className="font-medium text-gray-900">Overall Marketing Metrics</span>
                  <p className="text-gray-500 mt-1">
                    Total leads, new members, revenue, ROI, and projected lifetime value
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <span className="font-medium text-gray-900">Google Guarantee (GLSA)</span>
                  <p className="text-gray-500 mt-1">
                    Qualified leads, conversions, projected revenue from paid ads
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <span className="font-medium text-gray-900">Organic SEO Performance</span>
                  <p className="text-gray-500 mt-1">
                    GBP leads, organic revenue, website clicks, and current ROI
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <span className="font-medium text-gray-900">Profile Ranking</span>
                  <p className="text-gray-500 mt-1">
                    Keyword positions, visibility scores, heatmap grids, competitor comparison
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
